import { useMemo } from 'react'
import { DEFAULT_ADVANCED_SETTINGS, useStalledAfterDays } from './AdvancedSettingsProvider'
import { wholeDaysSince } from '../format'
import { useAdvancedOverviewState } from './AdvancedOverviewProvider'
import {
  AdvancedContentCompletion,
  AdvancedPatientOverview,
  AdvancedPatientSummary,
  AdvancedReflection,
  AdvancedTrackSummary,
} from './types'

// `AdvancedPatientView` owns the only loading branch, so this throw is an invariant, not a reachable path.
const useReadyOverview = (): AdvancedPatientOverview => {
  const state = useAdvancedOverviewState()

  if (state.status !== 'ready') {
    throw new Error('Advanced panels render only inside the ready branch of AdvancedPatientView')
  }

  return state.overview
}

export const usePatientSummary = (): AdvancedPatientSummary => useReadyOverview().summary

export const useTracks = (): AdvancedTrackSummary[] => useReadyOverview().summary.tracks

export const useCompletions = (): AdvancedContentCompletion[] => useReadyOverview().completions

export const useReflections = (): AdvancedReflection[] => useReadyOverview().reflections

// Display policy on dates the service returns (ADR-064).
export const STALLED_AFTER_DAYS = DEFAULT_ADVANCED_SETTINGS.stalledAfterDays

const laterIso = (left: string | null, right: string): string => (!left || right > left ? right : left)

// Accepted and past its start date; an intake-stage or not-yet-started track has no clock to run (ADR-097).
export type RunningTrack = AdvancedTrackSummary & { startedAt: string }

export const isTrackRunning = (track: AdvancedTrackSummary, now = new Date()): track is RunningTrack =>
  track.stage === 'accepted' && track.startedAt !== null && !track.endedAt && track.startedAt <= now.toISOString()

export const lastCompletedThisRun = (track: AdvancedTrackSummary): string | null =>
  track.startedAt && track.lastCompletedAt && track.lastCompletedAt >= track.startedAt ? track.lastCompletedAt : null

export const quietFrom = (track: AdvancedTrackSummary): string | null =>
  lastCompletedThisRun(track) ?? (isTrackRunning(track) ? track.startedAt : null)

export const lastCompletedThisRunFor = (summary: AdvancedPatientSummary): string | null =>
  summary.tracks.reduce<string | null>((latest, track) => {
    const at = lastCompletedThisRun(track)

    return at ? laterIso(latest, at) : latest
  }, null)

export const quietFromPatient = (summary: AdvancedPatientSummary): string | null => {
  const last = lastCompletedThisRunFor(summary)
  if (last) {
    return last
  }

  return summary.tracks
    .filter((track): track is RunningTrack => isTrackRunning(track))
    .reduce<string | null>(
      (earliest, track) => (!earliest || track.startedAt < earliest ? track.startedAt : earliest),
      null
    )
}

export const isTrackStalled = (track: AdvancedTrackSummary, stalledAfterDays = STALLED_AFTER_DAYS): boolean => {
  if (!isTrackRunning(track)) {
    return false
  }

  const days = wholeDaysSince(quietFrom(track))

  return days !== null && days >= stalledAfterDays
}

export type Attention = {
  daysSinceLastCompletion: number | null
  activeTrackCount: number
  stalledTracks: string[]
}

export const deriveAttention = (summary: AdvancedPatientSummary, stalledAfterDays = STALLED_AFTER_DAYS): Attention => ({
  daysSinceLastCompletion: wholeDaysSince(lastCompletedThisRunFor(summary)),
  activeTrackCount: summary.activeTrackCount,
  stalledTracks: summary.tracks
    .filter((track) => isTrackStalled(track, stalledAfterDays))
    .map((track) => track.trackKey),
})

export const compareCaseloadAttention = (
  a: AdvancedPatientSummary,
  b: AdvancedPatientSummary,
  stalledAfterDays = STALLED_AFTER_DAYS
): number => {
  const rank = (patient: AdvancedPatientSummary) => {
    if (patient.tracks.length === 0) {
      return { hasTracks: 0, stalled: 0, quiet: 0 }
    }

    return {
      hasTracks: 1,
      stalled: deriveAttention(patient, stalledAfterDays).stalledTracks.length,
      quiet: wholeDaysSince(quietFromPatient(patient)) ?? 0,
    }
  }

  const rankA = rank(a)
  const rankB = rank(b)

  if (rankA.hasTracks !== rankB.hasTracks) {
    return rankB.hasTracks - rankA.hasTracks
  }
  if (rankA.stalled !== rankB.stalled) {
    return rankB.stalled - rankA.stalled
  }

  return rankB.quiet - rankA.quiet
}

export const useAttention = (): Attention => {
  const summary = usePatientSummary()
  const stalledAfterDays = useStalledAfterDays()

  return useMemo(() => deriveAttention(summary, stalledAfterDays), [summary, stalledAfterDays])
}

export type CompletionsByTrack = Array<{ trackKey: string; completions: AdvancedContentCompletion[] }>

// Orphan trackKeys (no current enrolment) are appended, not dropped.
export const groupCompletionsByTrack = (
  tracks: AdvancedTrackSummary[],
  completions: AdvancedContentCompletion[]
): CompletionsByTrack => {
  const known = new Set(tracks.map((track) => track.trackKey))
  const enrolled = tracks.map((track) => ({
    trackKey: track.trackKey,
    completions: completions.filter((completion) => completion.trackKey === track.trackKey),
  }))
  const orphanKeys = [...new Set(completions.map((item) => item.trackKey))].filter((key) => !known.has(key))

  return [
    ...enrolled,
    ...orphanKeys.map((trackKey) => ({
      trackKey,
      completions: completions.filter((item) => item.trackKey === trackKey),
    })),
  ]
}

export const advancedTooltip = (summary: AdvancedPatientSummary, stalledAfterDays = STALLED_AFTER_DAYS): string => {
  if (summary.tracks.length === 0) {
    return 'Advanced programme · no tracks yet'
  }

  const stalled = deriveAttention(summary, stalledAfterDays).stalledTracks

  return stalled.length > 0
    ? `Advanced programme · ${summary.activeTrackCount} active · stalled: ${stalled.join(', ')}`
    : `Advanced programme · ${summary.activeTrackCount} active`
}
